import { useState } from 'react';
import type { ApExceptionRuleSet } from '@erp/rpc';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import {
  useCreateApExceptionRuleSet,
  useUpdateApExceptionRuleSet,
} from 'src/api/rpc/hooks/accounts-payable/useApExceptionRuleSets';
import type { ExceptionTypeOption } from './ExceptionRuleEditorRow';
import {
  availableOptionIds,
  draftFromRuleSet,
  draftProblem,
  dropStaleRules,
  emptyRuleSetDraft,
  moveRule,
  newRuleDraft,
  nextRuleOptionId,
  staleRules,
  toCreateInput,
  type RuleDraft,
  type RuleSetDraft,
} from './exceptionRuleSetDraft';

/**
 * State + handlers behind the rule set editor. The dialog only renders what
 * this returns, so draft mutations stay in one place and out of JSX.
 */
export function useRuleSetEditor(ruleSet: ApExceptionRuleSet | null) {
  const [draft, setDraft] = useState<RuleSetDraft>(() =>
    ruleSet ? draftFromRuleSet(ruleSet) : emptyRuleSetDraft(),
  );
  const create = useCreateApExceptionRuleSet();
  const update = useUpdateApExceptionRuleSet();
  const saving = create.isPending || update.isPending;

  const {
    data: enumsResponse,
    isLoading: enumsLoading,
    isError: enumsError,
    refetch: refetchEnums,
  } = useCoreEnums({ first: 100, includeOptions: true }, { staleTime: 30_000 });
  const coreEnums = enumsResponse?.edges;
  const enumsReady = enumsResponse !== undefined && !enumsError;
  const optionsOfEnum = (enumId: string | null): ExceptionTypeOption[] =>
    (coreEnums?.find((coreEnum) => coreEnum.id === enumId)?.options ?? []).map(
      (option) => ({
        id: option.id,
        name: option.name ?? '',
        description: option.description ?? null,
      }),
    );

  const typeOptions = enumsReady
    ? (
        coreEnums?.find((coreEnum) => coreEnum.id === draft.exceptionEnumId)
          ?.options ?? []
      ).map((option) => ({
        id: option.id,
        name: option.name ?? '',
        description: option.description ?? null,
      }))
    : [];
  const optionIds = typeOptions.map((option) => option.id);
  const staleKeys = new Set(
    enumsReady
      ? staleRules(draft.rules, optionIds).map((rule) => rule.key)
      : [],
  );
  const problem = draftProblem(draft);
  const canSave =
    !saving && enumsReady && problem === null && staleKeys.size === 0;
  const canAddRule =
    enumsReady &&
    draft.exceptionEnumId !== null &&
    availableOptionIds(optionIds, draft.rules).length > 0;

  function setEnum(enumId: string | null) {
    const nextOptionIds = optionsOfEnum(enumId).map((option) => option.id);
    setDraft({
      ...draft,
      exceptionEnumId: enumId,
      // Rules that no longer resolve under the new enum are dropped rather
      // than left to fail validation on save.
      rules: enumId ? dropStaleRules(draft.rules, nextOptionIds) : [],
    });
  }

  function addRule(optionId?: string) {
    const chosen = nextRuleOptionId(
      availableOptionIds(optionIds, draft.rules),
      optionId,
    );
    setDraft({
      ...draft,
      rules: [...draft.rules, newRuleDraft(chosen)],
    });
  }

  function addRemainingRules() {
    const remaining = availableOptionIds(optionIds, draft.rules);
    if (remaining.length === 0) return;
    setDraft({
      ...draft,
      rules: [...draft.rules, ...remaining.map((id) => newRuleDraft(id))],
    });
  }

  function updateRule(next: RuleDraft) {
    setDraft({
      ...draft,
      rules: draft.rules.map((rule) => (rule.key === next.key ? next : rule)),
    });
  }

  function moveRuleAt(index: number, direction: -1 | 1) {
    setDraft({ ...draft, rules: moveRule(draft.rules, index, direction) });
  }

  function removeRule(key: string) {
    setDraft({ ...draft, rules: draft.rules.filter((r) => r.key !== key) });
  }

  /** Types a given rule may still pick: unused ones plus its own. */
  function typeOptionsFor(rule: RuleDraft): ExceptionTypeOption[] {
    const allowed = new Set(availableOptionIds(optionIds, draft.rules, rule));
    return typeOptions.filter((option) => allowed.has(option.id));
  }

  async function save(): Promise<string | null> {
    if (!canSave) return null;
    const input = toCreateInput(draft);
    if (ruleSet) {
      await update.mutateAsync({ id: ruleSet.id, ...input });
      return ruleSet.id;
    }
    const created = await create.mutateAsync(input);
    return created.id;
  }

  return {
    draft,
    setDraft,
    saving,
    canSave,
    canAddRule,
    staleKeys,
    typeOptions,
    enumsLoading,
    enumsError,
    refetchEnums,
    setEnum,
    addRule,
    addRemainingRules,
    updateRule,
    moveRuleAt,
    removeRule,
    typeOptionsFor,
    save,
  };
}
